import 'highcharts';

import { type Options, type PointOptionsObject, type SeriesSolidgaugeOptions } from 'highcharts';
import { useEffect } from 'react';

import { COLOR_GRAY_500 } from '@/styles/palette';

import { useFlareContext } from '../FlareContext';
import DonutLoader from '../loaders/DonutLoader';
import {
  type FlareDataPointOptions,
  type FlareSeriesOptions,
  type SanitizedFlareProps,
} from '../types';

type DonutProps = {
  innerSize?: number;
  backgroundColor?: string;
  rounded?: boolean;
  y?: string;
};

const buildDonutOptions = (
  options: Options,
  parentProps: SanitizedFlareProps,
  props: DonutProps,
): Options => {
  const { data, colors } = parentProps;
  const { y = 'y', innerSize = 0.65, backgroundColor = COLOR_GRAY_500, rounded = false } = props;

  const series: SeriesSolidgaugeOptions[] | undefined = data?.map((series: FlareSeriesOptions) => {
    const { name, data: seriesData, ...rest } = series;

    const newSeriesData = seriesData.map((item: FlareDataPointOptions, pointIndex: number) => {
      const yVal = item[y];

      const newItem: PointOptionsObject = {
        ...item,
        y: yVal,
        color: colors[pointIndex],
        radius: '110%',
        innerRadius: innerSize * 100 + '%',
      };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newItem[y as keyof PointOptionsObject];

      return newItem;
    });

    return {
      type: 'solidgauge',
      ...rest,
      name,
      data: newSeriesData,
    };
  });

  return {
    ...options,
    chart: {
      ...options.chart,
      type: 'solidgauge',
    },
    pane: {
      startAngle: 0,
      endAngle: 360,
      background: [
        {
          outerRadius: '110%',
          innerRadius: innerSize * 100 + '%',
          backgroundColor,
          borderWidth: 0,
        },
      ],
    },
    yAxis: {
      min: 0,
      max: 1,
      lineWidth: 0,
      tickPositions: [],
    },
    series,
    title: {
      ...options.title,
      verticalAlign: 'middle',
    },
    plotOptions: {
      ...options.plotOptions,
      solidgauge: {
        rounded,
        stickyTracking: false,
        dataLabels: {
          enabled: false,
        },
      },
    },
  };
};

const Gauge = (props: DonutProps) => {
  const { id, registerChild, isLoading } = useFlareContext();

  useEffect(() => {
    registerChild(id, (options: Options, parentProps: SanitizedFlareProps) =>
      buildDonutOptions(options, parentProps, props),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <DonutLoader style={{ flex: 1 }} />;
  }

  return null;
};

export default Gauge;
